import React from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";
import { Box, Rating, useTheme } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";

export interface GoogleRatingListing {
  rating?: number | null;
  reviewCount: number;
  reviewsUrl: string;
}

export interface GoogleRatingProps {
  listing?: GoogleRatingListing | null;
  /** "compact" is a single star plus the numbers, for tables. */
  variant?: "full" | "compact";
  /** "onDark" matches dark surfaces such as the quote action card. */
  tone?: "default" | "onDark";
}

/**
 * A vendor's live Google rating, linked to its Maps listing. Renders nothing
 * without a rating and at least one review, so no placeholder stars ever show.
 */
const GoogleRating: React.FC<GoogleRatingProps> = ({
  listing,
  variant = "full",
  tone = "default",
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const format = useFormat();

  if (!listing?.rating || listing.reviewCount <= 0) {
    return null;
  }

  const { rating, reviewCount, reviewsUrl } = listing;
  const onDark = tone === "onDark";
  const mutedColor = onDark
    ? theme.palette.neutralAlpha.white55
    : theme.palette.text.secondary;

  return (
    <Box
      component="a"
      href={reviewsUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("ui.rating_aria", {
        rating: format.number(rating, { maximumFractionDigits: 1 }),
        count: reviewCount,
      })}
      // Sits inside clickable cards and table rows.
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 0.75,
        rowGap: 0.25,
        color: mutedColor,
        fontSize: onDark ? "1.2rem" : "inherit",
        textDecoration: "none",
        "&:hover .google-rating-count": { textDecoration: "underline" },
      }}
    >
      {variant === "compact" ? (
        <StarIcon
          sx={{ color: theme.palette.warning.main, fontSize: "1.1rem" }}
        />
      ) : (
        <Rating
          value={rating}
          precision={0.1}
          size={onDark ? "large" : "medium"}
          readOnly
          sx={{
            color: theme.palette.warning.main,
            "& .MuiRating-iconEmpty": {
              color: onDark
                ? theme.palette.neutralAlpha.white30
                : theme.palette.action.disabled,
            },
          }}
        />
      )}
      <Box component="span" sx={{ fontWeight: 700 }}>
        {format.number(rating, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })}
      </Box>
      <Box component="span" className="google-rating-count">
        ({format.number(reviewCount)})
      </Box>
      {/* Google requires this attribution beside Places data shown without a map. */}
      <Box
        component="span"
        sx={{
          fontFamily: "Roboto, Arial, sans-serif",
          fontSize: "12px",
          whiteSpace: "nowrap",
        }}
      >
        Google Maps
      </Box>
    </Box>
  );
};

export default GoogleRating;
